/**
 * Shared date-validation helpers used by the submit-claim and edit-claim Zod
 * schemas so Date of Birth / Date of Service rules behave identically on both
 * forms (feature 034). Moved here from edit-claim/schema.ts.
 */

import dayjs from 'dayjs';

/**
 * Parse a date string as a local date (avoids UTC-midnight timezone shift).
 * Supports multiple formats:
 * - YYYY-MM-DD (ISO format from date picker)
 * - MM/DD/YYYY or MM/DD/YY (user typed format)
 * Returns NaN Date for invalid or unparseable dates.
 */
export function parseLocalDate(s: string): Date {
  if (!s) return new Date(NaN);

  // Try ISO format first (YYYY-MM-DD or YYYY-MM-DDTHH:MM:SSZ)
  if (s.includes('-')) {
    // Strip time portion if present (e.g., "2020-02-02T05:00:00Z" -> "2020-02-02")
    const datePart = s.split('T')[0];
    const [y, m, d] = datePart.split('-').map(Number);
    if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
      const date = new Date(y, m - 1, d);
      // Validate: check that the date components match (catches invalid dates like 02-31)
      if (date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d) {
        return date;
      }
      return new Date(NaN);
    }
  }

  // Try US format (MM/DD/YYYY or MM/DD/YY)
  if (s.includes('/')) {
    const [m, d, y] = s.split('/').map(Number);
    if (!isNaN(m) && !isNaN(d) && !isNaN(y)) {
      // Handle 2-digit year with pivot: 00-29 -> 2000s, 30-99 -> 1900s
      const fullYear = y < 100 ? (y < 30 ? 2000 + y : 1900 + y) : y;
      const date = new Date(fullYear, m - 1, d);
      // Validate: check that the date components match (catches invalid dates like 02/31)
      if (date.getFullYear() === fullYear && date.getMonth() === m - 1 && date.getDate() === d) {
        return date;
      }
      return new Date(NaN);
    }
  }

  // No fallback - return invalid date for unrecognized formats
  return new Date(NaN);
}

/** Today at local midnight — for date-only comparisons without time-of-day drift. */
export function today(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Check if a Date is valid (not NaN) */
export function isValidDate(d: Date): boolean {
  return !isNaN(d.getTime());
}

/** Format an ISO ('YYYY-MM-DD') date string as 'MM/DD/YYYY' for display in an
 *  AI-suggestion's `displayValue` (e.g. a demographic date-of-birth suggestion or the
 *  Coding block's anesthesiaEndDate suggestion); returns the raw value if unparseable. */
export function formatDateForDisplay(value: string): string {
  const d = dayjs(value);
  return d.isValid() ? d.format('MM/DD/YYYY') : value;
}
